/**
 * Macro calendar.
 *
 * High-impact rows are highlighted because they are the ones that actually
 * gate trading: `event_proximity` vetoes entries around them.
 */

import { useEvents } from '@/hooks/useQueries'

const IMPACT_TONE = {
  high: 'bg-down/20 text-down',
  med: 'bg-warn/20 text-warn',
  low: 'bg-panel-hover text-ink-dim',
} as const

export function EventList() {
  const { data, isLoading } = useEvents()

  if (isLoading) return <p className="p-3 text-2xs text-ink-dim">Đang tải lịch…</p>
  if (!data?.length) return <p className="p-3 text-2xs text-ink-dim">Không có sự kiện trong 96h tới</p>

  return (
    <ul className="divide-y divide-border">
      {data.map((event) => {
        const imminent = (event.hours_until ?? 99) >= 0 && (event.hours_until ?? 99) <= 2
        return (
          <li key={event.event_id} className="flex items-center gap-2 px-3 py-1.5 hover:bg-panel-hover">
            <span className={`rounded px-1.5 py-0.5 text-2xs ${IMPACT_TONE[event.impact]}`}>
              {event.impact}
            </span>
            <span className="w-10 font-mono text-2xs text-ink-dim">{event.country}</span>
            <span className="flex-1 text-xs text-ink/90">{event.name}</span>

            {event.forecast !== null && (
              <span className="font-mono text-2xs text-ink-dim">
                dự báo {event.forecast}
                {event.previous !== null && ` · trước ${event.previous}`}
              </span>
            )}

            <span
              className={`w-24 text-right font-mono text-2xs ${imminent ? 'text-warn' : 'text-ink-dim'}`}
              title={new Date(event.event_time).toLocaleString('vi-VN')}
            >
              {formatCountdown(event.hours_until)}
            </span>

            {imminent && event.impact === 'high' && (
              <span title="Engine đang chặn vào lệnh quanh mốc này" className="text-warn">⛔</span>
            )}
          </li>
        )
      })}
    </ul>
  )
}

function formatCountdown(hours: number | null): string {
  if (hours === null) return '—'
  if (hours < 0) return `${Math.abs(hours).toFixed(1)}h trước`
  if (hours < 1) return `${Math.round(hours * 60)} phút nữa`
  if (hours < 24) return `${hours.toFixed(1)}h nữa`
  return `${Math.round(hours / 24)} ngày nữa`
}
